import { getPostMediaTypes } from "@/lib/booru-media-types"
import type { BooruContentTypeVisibility, BooruPost, BooruVisibilityType } from "@/lib/booru-api"
import { normalizePostRating, postMatchesRatings, ALL_FILTER_RATINGS, type FilterRating } from "@/lib/rating-filter"

type MediaPost = {
  fileUrl?: string | null
  previewUrl?: string | null
  sampleUrl?: string | null
  albumImages?: string[] | null
}

/** Whether a post has any image address to draw. Danbooru gives guests no addresses for posts still waiting for
 * approval (is_pending), and a grid cell for one would be a blank box. */
export function postHasShowableImage(post: MediaPost): boolean {
  return Boolean(post.fileUrl || post.previewUrl || post.sampleUrl || post.albumImages?.length)
}

type RatedPost = Parameters<typeof postMatchesRatings>[0]

/** Of the posts the rating filter removed, how many there were and which everyday ratings they carry. Extreme
 * posts are left out: they follow the separate Unfiltered Content switch, not the rating pills. */
export function summarizeHiddenByRating(
  posts: RatedPost[],
  activeRatings: FilterRating[],
  unfilteredAllowed: boolean,
): { count: number; ratings: FilterRating[] } {
  const hidden = new Set<FilterRating>()
  let count = 0
  for (const post of posts) {
    if (postMatchesRatings(post, activeRatings, unfilteredAllowed)) continue
    const category = post.isNsfwu ? "unfiltered" : normalizePostRating(post.rating, post.site)
    if (category === "unfiltered") continue
    count += 1
    hidden.add(category)
  }
  return { count, ratings: ALL_FILTER_RATINGS.filter((rating) => hidden.has(rating)) }
}

/** Of the posts the Content Filters (image / video / album) removed: how many, and which of those types they hold, so the
 * empty screen can switch just those back on. A post stays visible when ANY of its types is on (see getPostMediaTypes). */
export function summarizeHiddenByContentType(
  posts: BooruPost[],
  visibility: BooruContentTypeVisibility,
): { count: number; types: BooruVisibilityType[] } {
  const hidden = new Set<BooruVisibilityType>()
  let count = 0
  for (const post of posts) {
    const types = getPostMediaTypes(post)
    if ([...types].some((type) => visibility[type])) continue
    count += 1
    types.forEach((type) => hidden.add(type))
  }
  return { count, types: (["image", "video", "album"] as const).filter((type) => hidden.has(type)) }
}
